//core
import { createTheme, Palette } from '@mui/material/styles';
import { Theme, ThemeOptions } from "@mui/material/styles/createTheme";
import { formLabelClasses, outlinedInputClasses } from "@mui/material";
import { MuiButtonsOverrides } from "./general/buttonsOverrides";


type AppThemeType = string;

interface IAppThemes {
  [theme: string]: ThemeOptions;
};

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xlg: true;
  }
}

interface NavigationPalette {
  [key: string]: string;
}
interface OtherPalette {
  [key: string]: string;
}

interface GradientButtonPalette {
  [key: string]: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    white: Palette['primary'];
    navigation: NavigationPalette;
    selected: Palette['primary'];
    other: OtherPalette;
    gradientButton: GradientButtonPalette;
  }
  interface PaletteOptions {
    white: PaletteOptions['primary'];
    navigation: NavigationPalette;
    selected: PaletteOptions['primary'];
    other: OtherPalette;
    gradientButton: GradientButtonPalette;
  }
}

export const appTheme: AppThemeType = process.env.REACT_APP_THEME || 'pma_light';

export const themes: IAppThemes = {
  pma_light: {
    spacing: 8,
    //shadows: 'none',
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#009E69',
        contrastText: '#ffffff',
        dark: '#008F5F',
      },
      gradientButton: {
        background: '#009E69', //primary.main
        hover: '#008F5F', //primary.dark
      },
      secondary: {
        main: '#222',
        light: '#555555',
        dark: '#000',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#fff',
        menuHover: 'rgba(0, 0, 0, 0.04)',
        menuActive: 'rgba(0,158,105,0.08)',
        menuContrast: '#292621',
        menuContrastActive: '#009E69',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#fff',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: 'rgba(0, 0, 0, 0.54)',
        gradientTo: 'rgba(0, 0, 0, 0.54)',
        gradientActiveFrom: '#009E69',
        gradientActiveTo: '#009E69',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#fff',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      /*MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(34,34,34,0.8)',
          },
        },
      },*/
    },
  },
  pma_dark: {
    spacing: 8,
    //shadows: 'none',
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#009E69',
        contrastText: '#ffffff',
        dark: '#008F5F',
      },
      gradientButton: {
        background: '#009E69', //primary.main
        hover: '#008F5F', //primary.dark
      },
      secondary: {
        main: '#222',
        light: '#555555',
        dark: '#000',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#fff',
        menuHover: 'rgba(0, 0, 0, 0.04)',
        menuActive: 'rgba(0,158,105,0.08)',
        menuContrast: '#292621',
        menuContrastActive: '#009E69',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#fff',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: 'rgba(0, 0, 0, 0.54)',
        gradientTo: 'rgba(0, 0, 0, 0.54)',
        gradientActiveFrom: '#009E69',
        gradientActiveTo: '#009E69',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#fff',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      /*MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(34,34,34,0.8)',
          },
        },
      },*/
    },
  },
  pkmp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#FFC200',
        dark: '#F8BC00',
        light: '#FFCD2E',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#FFC200', //primary.main
        hover: '#F8BC00', //primary.dark
      },
      secondary: {
        main: '#222222',
        dark: '#000000',
        light: '#3E3E3E',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: '#1A1A1A',
        menuActive: 'rgba(255, 194, 0, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFC200',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1A1A1A',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#FFC200',
        gradientActiveTo: '#FFC200',
        scrollBg: 'rgba(0, 0, 0, 0.5)',
        scrollTrackBg: 'rgba(255,255,255,0.2)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      action: {
        //active: '#222222', // primary.light
        active: 'rgba(34, 34, 34, 0.54)',
        activatedOpacity: 0.54,
        hover: 'rgba(34, 34, 34, 0.04)', // primary.dark
        hoverOpacity: 0.04,
        selected: 'rgba(34, 34, 34, 0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(34, 34, 34, 0.26)', // text.disabled
        disabledOpacity: 0.25,
        disabledBackground: 'rgba(34, 34, 34, 0.12)',
        focus: 'rgba(5, 5, 27, 0.12)',
        focusOpacity: 0.12,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      /*MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: '#dda800', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: '#dda800', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: '#dda800', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: '#fff', //primary.main
          }
        }
      },*/
      MuiModal: {
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              //backgroundColor: 'rgba(34,34,34,0.8)',
            }
          },
        },
      },
      /*MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(34,34,34,0.8)', //secondary.main
          },
        },
      },*/
      /*MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'primary.main',
          }
        }
      },*/
    },
  },
  pkmp_dark: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#fff',
        secondary: '#D9D9D9',
        disabled: '#BDB8AD',
        // primary: '#292621',
        // secondary: '#403C34',
        // disabled: '#BDB8AD',
      },
      primary: {
        main: '#ffc200',
        contrastText: '#000',
        dark: '#dda800',
        light: '#fecc2d',
      },
      gradientButton: {
        background: '#ffc200', //primary.main
        hover: '#dda800', //primary.dark
      },
      secondary: {
        main: '#fff',
        light: '#f5f5f5',
        dark: '#e8e8e8',
        contrastText: '#000',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: '#1A1A1A',
        menuActive: 'rgba(255, 194, 0, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFC200',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1A1A1A',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#FFC200',
        gradientActiveTo: '#FFC200',
        scrollBg: 'rgba(0, 0, 0, 0.5)',
        scrollTrackBg: 'rgba(255,255,255,0.2)',
      },
      other: {
        outlinedBorder: '#dda800', //primary.main
      },
      selected: {
        main: '#2b2a2a',
      },
      action: {
        active: '#fff', // primary.light
        hover: 'rgba(255,255,255,0.08)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.16)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.3)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363535',
        default: '#000',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#f5f5f5',
        A200: '#eeeeee',
        A400: '#bdbdbd',
        A700: '#616161',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: '#dda800', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: '#dda800', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: '#dda800', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: '#fff', //primary.main
          }
        }
      },
      /*MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'primary.main',
          }
        }
      },*/
    },
  },
  afft_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#1D0909',
        light: '#292621',
        contrastText: '#ffffff',
        dark: '#1D0909',
      },
      gradientButton: {
        background: '#1D0909', //primary.main
        hover: '#1D0909', //primary.dark
      },
      secondary: {
        main: '#222',
        light: '#555',
        dark: '#000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#050403',
        menuHover: 'rgba(255, 255, 255, 0.06)',
        menuActive: 'rgba(255, 255, 255, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF291F',
        headerBg: '#050403',
        headerBorder: '#050403',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF291F',
        gradientActiveTo: '#FF291F',
        scrollBg: '#222',
        scrollTrackBg: 'rgba(255, 255, 255, 0.12)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Arimo', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  afft_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#E2E2E2',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#FF291F',
        light: '#FF5951',
        contrastText: '#ffffff',
        dark: '#E01107',
      },
      gradientButton: {
        background: '#FF291F', //primary.main
        hover: '#E01107', //primary.dark
      },
      secondary: {
        main: '#F8FAFF',
        light: '#FEFEFF',
        dark: '#ECEEF1',
        contrastText: 'rgba(0,0,0,0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#050403',
        menuHover: 'rgba(255, 255, 255, 0.06)',
        menuActive: 'rgba(255, 255, 255, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF291F',
        headerBg: '#050403',
        headerBorder: '#050403',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF291F',
        gradientActiveTo: '#FF291F',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.09)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#F8FAFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: '#151515',
      },
      action: {
        active: 'rgba(226,226,226,0.54)', // primary.light
        hover: 'rgba(226,226,226,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(226,226,226,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(226,226,226,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(226,226,226, 0.12)',
        focus: 'rgba(226,226,226, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#1E1E1E',
        default: '#121212',
      },

      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Arimo', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F8FAFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F8FAFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F8FAFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F8FAFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      /*MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'primary.main',
          }
        }
      },*/
    },
  },
  grp_light: {
    spacing: 8,
    //shadows: 'none',
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#0066FF',
        light: '#2D81FF',
        contrastText: '#ffffff',
        dark: '#004CBF',
      },
      gradientButton: {
        background: '#0066FF', //primary.main
        hover: '#004CBF', //primary.dark
      },
      secondary: {
        main: '#222222',
        light: '#3E3E3E',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: '#1A1A1A',
        menuActive: 'rgba(1, 255, 19, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#01FF13',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1A1A1A',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#01FF13',
        gradientActiveTo: '#01FF13',
        scrollBg: '#222',
        scrollTrackBg: 'rgba(255, 255, 255, 0.12)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  grp_dark: {
    spacing: 8,
    //shadows: 'none',
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#0066FF',
        light: '#2D81FF',
        contrastText: '#ffffff',
        dark: '#004CBF',
      },
      gradientButton: {
        background: '#0066FF', //primary.main
        hover: '#004CBF', //primary.dark
      },
      secondary: {
        main: '#222222',
        light: '#3E3E3E',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: '#1A1A1A',
        menuActive: 'rgba(1, 255, 19, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#01FF13',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1A1A1A',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#01FF13',
        gradientActiveTo: '#01FF13',
        scrollBg: '#222',
        scrollTrackBg: 'rgba(255, 255, 255, 0.12)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F4F1',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  bat_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#3FB5AB',
        dark: '#3FB5AB',
        light: '#57D4CA',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(90deg, #3FB5AB 0%, #19CDA1 100%);', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(6, 233, 157, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#06E99D',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#06E99D',
        gradientActiveTo: '#06E99D',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85,85,85,0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(0, 0, 0, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(0,0,0,0.54)',
        hover: 'rgba(0,0,0,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(0,0,0,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0,0,0,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(0,0,0,0.12)',
        focus: 'rgba(0,0,0, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  bat_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.7)',
        disabled: 'rgba(255, 255, 255, 0.5)',
      },
      primary: {
        main: '#06E99D',
        dark: '#01CB87',
        light: '#0EFDAD',
        contrastText: '#E1FFFC',
      },
      gradientButton: {
        background: 'linear-gradient(90deg, #3FB5AB 0%, #19CDA1 100%);', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(6, 233, 157, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#06E99D',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#06E99D',
        gradientActiveTo: '#06E99D',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85,85,85,0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255,255,255,0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)',
        hover: 'rgba(255,255,255,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(255,255,255,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(255,255,255,0.12)',
        focus: 'rgba(255,255,255,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#06E99D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#06E99D', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#06E99D !important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#06E99D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#06E99D', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#06E99D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#06E99D', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#06E99D' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  mjp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#3EA1D9',
        light: '#88CCF2',
        contrastText: '#ffffff',
        dark: '#3790C3',
      },
      gradientButton: {
        background: '#3EA1D9', //primary.main
        hover: '#3790C3', //primary.dark
      },
      secondary: {
        main: '#222222',
        light: '#3E3E3E',
        dark: '#000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#152C49',
        menuHover: 'rgba(62, 161, 217, 0.08)',
        menuActive: 'rgba(62, 161, 217, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#3EA1D9',
        headerBg: '#152C49',
        headerBorder: '#152C49',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(21, 44, 73, 1)',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#3EA1D9',
        gradientActiveTo: '#3EA1D9',
        scrollBg: '#222',
        scrollTrackBg: 'rgba(255, 255, 255, 0.12)',
      },
      other: {
        outlinedBorder: 'rgba(34, 34, 34, 0.26)',
      },
      selected: {
        main: '#ebf7f3',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Arimo', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  mjp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#E2E2E2',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#FF291F',
        light: '#FF5951',
        contrastText: '#ffffff',
        dark: '#E01107',
      },
      gradientButton: {
        background: '#FF291F', //primary.main
        hover: '#E01107', //primary.dark
      },
      secondary: {
        main: '#F8FAFF',
        light: '#FEFEFF',
        dark: '#ECEEF1',
        contrastText: 'rgba(0,0,0,0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#050403',
        menuHover: 'rgba(0, 0, 0, 0.04)',
        menuActive: 'rgba(255, 255, 255, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF291F',
        headerBg: '#050403',
        headerBorder: '#050403',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF291F',
        gradientActiveTo: '#FF291F',
        scrollBg: '#222',
        scrollTrackBg: 'rgba(255, 255, 255, 0.12)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.09)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#F8FAFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: '#151515',
      },
      action: {
        active: 'rgba(226,226,226,0.54)', // primary.light
        hover: 'rgba(226,226,226,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(226,226,226,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(226,226,226,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(226,226,226, 0.12)',
        focus: 'rgba(226,226,226, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#1E1E1E',
        default: '#121212',
      },
      grey: {
        50: '#303030',
        100: '#424242',
        200: '#616161',
        300: '#424242',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      }
    },
    typography: {
      fontFamily: `'Arimo', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F8FAFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F8FAFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F8FAFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F8FAFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F8FAFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      /*MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'primary.main',
          }
        }
      },*/
    },
  },
  wno_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#2896E6',
        dark: '#1D85D2',
        light: '#4DB0F7',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#2896E6', //primary.main
        hover: '#1D85D2', //primary.dark
      },
      secondary: {
        main: '#222222',
        dark: '#000000',
        light: '#3E3E3E',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#000000',
      },
      navigation: {
        menuBg: '#0F2445',
        menuHover: 'rgba(40, 150, 230, 0.04)',
        menuActive: 'rgba(40, 150, 230, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#2896E6',
        headerBg: '#0F2445',
        headerBorder: '#0F2445',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#2896E6',
        gradientActiveTo: '#2896E6',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255,0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(0, 0, 0, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(15,36,69,0.54)',
        hover: 'rgba(15,36,69,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(15,36,69,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(15,36,69,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(15,36,69,0.12)',
        focus: 'rgba(0,0,0,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  wno_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#2896E6',
        dark: '#1D85D2',
        light: '#4DB0F7',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#2896E6', //primary.main
        hover: '#1D85D2', //primary.dark
      },
      secondary: {
        main: '#F6F9FF',
        dark: '#E0E4EF',
        light: '#FFFFFF',
        contrastText: 'rgba(0,0,0,0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#0F2445',
        menuHover: 'rgba(40, 150, 230, 0.04)',
        menuActive: 'rgba(40, 150, 230, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#2896E6',
        headerBg: '#0F2445',
        headerBorder: '#0F2445',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#2896E6',
        gradientActiveTo: '#2896E6',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255,0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.09)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: '#151515',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2896E6', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#2896E6', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#2896E6!important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2896E6', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#2896E6', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2896E6', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#2896E6', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#2896E6' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  plds_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#70AE26',
        dark: '#67A121',
        light: '#96D34D',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#70AE26', //primary.main
        hover: '#67A121', //primary.dark
      },
      secondary: {
        main: '#231F20',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#000000',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(112, 174, 38, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#70AE26',
        headerBg: '#231F20',
        headerBorder: '#231F20',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#70AE26',
        gradientActiveTo: '#70AE26',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85,0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(0, 0, 0, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(0,0,0,0.54)',
        hover: 'rgba(0,0,0,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(0,0,0,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0,0,0,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(0,0,0,0.12)',
        focus: 'rgba(0,0,0,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  plds_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#70AE26',
        dark: '#67A121',
        light: '#96D34D',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#70AE26', //primary.main
        hover: '#67A121', //primary.dark
      },
      secondary: {
        main: '#F6F9FF',
        dark: '#E0E4EF',
        light: '#FFFFFF',
        contrastText: 'rgba(0,0,0,0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#231F20',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(112, 174, 38, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#70AE26',
        headerBg: '#231F20',
        headerBorder: '#231F20',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#70AE26',
        gradientActiveTo: '#70AE26',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255,0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: '#151515',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#70AE26', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#70AE26', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#70AE26!important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#70AE26', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#70AE26', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#70AE26', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#70AE26', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#70AE26' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  gca_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#3634EE',
        dark: '#1B19D5',
        light: '#5E5CFB',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#3634EE', //primary.main
        hover: '#1B19D5', //primary.dark
      },
      secondary: {
        main: '#222222',
        dark: '#000000',
        light: '#3E3E3E',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#000000',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(198, 239, 61, 0.08)',
        menuActive: 'rgba(198, 239, 61, 0.12)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#C6EF3D',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#C6EF3D',
        gradientActiveTo: '#C6EF3D',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(34, 34, 34, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(0,0,0,0.54)',
        hover: 'rgba(0,0,0,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(0,0,0,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0,0,0,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(0,0,0,0.12)',
        focus: 'rgba(0,0,0,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  gca_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#C6EF3D',
        dark: '#ACD81B',
        light: '#DBFE69',
        contrastText: '#222222',
      },
      gradientButton: {
        background: '#C6EF3D', //primary.main
        hover: '#ACD81B', //primary.dark
      },
      secondary: {
        main: '#F6F9FF',
        dark: '#E0E4EF',
        light: '#FFFFFF',
        contrastText: 'rgba(0,0,0,0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(198, 239, 61, 0.08)',
        menuActive: 'rgba(198, 239, 61, 0.12)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#C6EF3D',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#EEEEEE',
        gradientTo: '#EEEEEE',
        gradientActiveFrom: '#C6EF3D',
        gradientActiveTo: '#C6EF3D',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: '#151515',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#151515',
        default: '#1E1D1D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#C6EF3D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#C6EF3D', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#C6EF3D !important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#C6EF3D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#C6EF3D', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#C6EF3D', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#C6EF3D', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#C6EF3D' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  lxa_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#580151',
        dark: '#1C041C',
        light: '#BF95D2',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#580151', //primary.main
        hover: '#1C041C', //primary.dark
      },
      secondary: {
        main: '#222222',
        dark: '#000000',
        light: '#3E3E3E',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#1C041C',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 255, 255, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFFFFF',
        headerBg: '#580151',
        headerBorder: '#580151',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: '#BF95D2',
        gradientTo: '#BF95D2',
        gradientActiveFrom: '#FFFFFF',
        gradientActiveTo: '#FFFFFF',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(34, 34, 34, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(34,34,34,0.54)',
        hover: 'rgba(34,34,34,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(34,34,34,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(34,34,34,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(34,34,34,0.12)',
        focus: 'rgba(34,34,34,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F6F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  lxa_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#39DFD4',
        dark: '#10C4B8',
        light: '#61F1E7',
        contrastText: '#070707',
      },
      gradientButton: {
        background: '#39DFD4', //primary.main
        hover: '#10C4B8', //primary.dark
      },
      secondary: {
        main: '#FAFCFF',
        dark: '#F6F6F6',
        light: '#FFFFFF',
        contrastText: '#000000',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#1C041C',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 255, 255, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFFFFF',
        headerBg: '#580151',
        headerBorder: '#580151',
        headerContrast: '#D8B7E7',
        headerContrastHover: 'rgba(216, 183, 231, 0.08)',
        gradientFrom: '#BF95D2',
        gradientTo: '#BF95D2',
        gradientActiveFrom: '#FFFFFF',
        gradientActiveTo: '#FFFFFF',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255,255,255,0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)',
        hover: 'rgba(255,255,255,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(255,255,255,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(255,255,255,0.12)',
        focus: 'rgba(255,255,255,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#1C041C',
        default: '#2D1329',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#39DFD4', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#39DFD4', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#39DFD4 !important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#39DFD4', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#39DFD4', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#39DFD4', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#39DFD4', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#39DFD4' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  mbp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#E72786',
        dark: '#DB1074',
        light: '#F73C99',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#E72786', //primary.main
        hover: '#DB1074', //primary.dark
      },
      secondary: {
        main: '#222222',
        dark: '#000000',
        light: '#3E3E3E',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#1A1A1A',
        menuHover: 'rgba(219, 16, 116, 0.5)',
        menuActive: 'rgba(219, 16, 116, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#DB1074',
        headerBg: '#1A1A1A',
        headerBorder: '#1A1A1A',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: 'rgba(255, 255, 255, 0.88)',
        gradientTo: 'rgba(255, 255, 255, 0.88)',
        gradientActiveFrom: '#DB1074',
        gradientActiveTo: '#DB1074',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      divider: 'rgba(34, 34, 34, 0.12)',
      selected: {
        main: 'rgba(0,0,0,0.08)',
      },
      action: {
        active: 'rgba(34,34,34,0.54)',
        hover: 'rgba(34,34,34,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(34,34,34,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(34,34,34,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(34,34,34,0.12)',
        focus: 'rgba(34,34,34,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#FFFFFF',
        default: '#F5F6F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(0,0,0,0.54)', //primary.main
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(0,0,0,0.54)', //primary.main
              },
            },
          },
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'rgba(0,0,0,0.54)', //primary.main
          }
        }
      },
    },
  },
  mbp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: '#AFAFAF',
        disabled: '#777575',
      },
      primary: {
        main: '#E72786',
        dark: '#DB1074',
        light: '#F73C99',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#E72786', //primary.main
        hover: '#DB1074', //primary.dark
      },
      secondary: {
        main: '#FAFCFF',
        dark: '#F6F6F6',
        light: '#FFFFFF',
        contrastText: '#000000',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#FF9800',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#1A1A1A',
        menuHover: 'rgba(219, 16, 116, 0.5)',
        menuActive: 'rgba(219, 16, 116, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#DB1074',
        headerBg: '#1A1A1A',
        headerBorder: '#1A1A1A',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.08)',
        gradientFrom: 'rgba(255, 255, 255, 0.88)',
        gradientTo: 'rgba(255, 255, 255, 0.88)',
        gradientActiveFrom: '#DB1074',
        gradientActiveTo: '#DB1074',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255,255,255,0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)',
        hover: 'rgba(255,255,255,0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(255,255,255,0.26)',
        disabledOpacity: 0.26,
        disabledBackground: 'rgba(255,255,255,0.12)',
        focus: 'rgba(255,255,255,0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#1A1A1A',
        default: '#0F0F0F',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
      //fontFamily: `'Wix Madefor Display', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E72786', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E72786', //primary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E72786 !important', //primary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E72786', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E72786', //primary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E72786', //primary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: 'rgba(255,255,255,1)', //primary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E72786', //primary.main
              },
            }
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E72786' //primary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
    },
  },
  btwnp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#1F5254',
        dark: '#154547',
        light: '#316E71',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#1F5254', //primary.main
        hover: '#154547', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#03242D',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(45, 236, 118, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#2DEC76',
        headerBg: '#03242D',
        headerBorder: '#03242D',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#2DEC76',
        gradientActiveTo: '#2DEC76',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  btwnp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#2DEC76',
        dark: '#10D45B',
        light: '#0EF5EF',
        contrastText: '#000000',
      },
      gradientButton: {
        background: '#2DEC76', //primary.main
        hover: '#10D45B', //primary.dark
      },
      secondary: {
        main: '#FFFBE4',
        light: '#FFFEEF',
        dark: '#FFFAD4',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#03242D',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(45, 236, 118, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#2DEC76',
        headerBg: '#03242D',
        headerBorder: '#03242D',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#2DEC76',
        gradientActiveTo: '#2DEC76',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#03242D',
        default: '#16323A',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2DEC76', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#2DEC76', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#2DEC76', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#2DEC76!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2DEC76', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#2DEC76', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#2DEC76', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#2DEC76', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#2DEC76' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  joinb_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#00A3FF',
        dark: '#0094E7',
        light: '#2AB2FF',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #00A3FF 0%, #00A3FF 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#243948',
        menuHover: '#1C2E3A',
        menuActive: 'rgba(218, 221, 225, 0.1)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#00A3FF',
        headerBg: '#243948',
        headerBorder: '#243948',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(218, 221, 225, 0.1)',
        gradientFrom: '#94A2AC',
        gradientTo: '#94A2AC',
        gradientActiveFrom: '#00A3FF',
        gradientActiveTo: '#00A3FF',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F3F4F6',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  pmaff_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F2F740 0%, #F2F740 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  pmaff_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F8FF13 0%, #F8FF13 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        light: '#EFF3FF',
        dark: '#D4DFFF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#171716',
        default: '#262626',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#17AB71!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#17AB71' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  btfp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#B61839',
        dark: '#970C29',
        light: '#E12E54',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#B61839', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(182, 24, 57, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#B61839',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#B61839',
        gradientActiveTo: '#B61839',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  btfp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#3C6DD6',
        dark: '#2550AD',
        light: '#83A9FC',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#3C6DD6', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        light: '#EFF3FF',
        dark: '#D4DFFF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(182, 24, 57, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#B61839',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#B61839',
        gradientActiveTo: '#B61839',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#3C6DD6', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#3C6DD6', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#3C6DD6', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#3C6DD6!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#3C6DD6', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#3C6DD6', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#3C6DD6', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#3C6DD6', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#3C6DD6' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  frgp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#F13E56',
        dark: '#D1112B',
        light: '#FF6C80',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#F13E56', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#1C1E2D',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(241, 62, 86, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F13E56',
        headerBg: '#1C1E2D',
        headerBorder: '#1C1E2D',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2C2F44',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F13E56',
        gradientActiveTo: '#F13E56',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  frgp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#F13E56',
        dark: '#D1112B',
        light: '#FF6C80',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#F13E56', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        light: '#EFF3FF',
        dark: '#D4DFFF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#1C1E2D',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(241, 62, 86, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F13E56',
        headerBg: '#1C1E2D',
        headerBorder: '#1C1E2D',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2C2F44',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F13E56',
        gradientActiveTo: '#F13E56',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#1C1E2D',
        default: '#131522',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F13E56', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F13E56', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F13E56', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F13E56!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F13E56', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F13E56', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F13E56', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F13E56', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F13E56' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  fbta_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#DDC56F',
        dark: '#C5AB50',
        light: '#EAD37F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#DDC56F', //primary.main
        hover: '#C5AB50', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(221, 197, 111, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#DDC56F',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#DDC56F',
        gradientActiveTo: '#DDC56F',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  fbta_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#DDC56F',
        dark: '#C5AB50',
        light: '#EAD37F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#DDC56F', //primary.main
        hover: '#C5AB50', //primary.dark
      },
      secondary: {
        main: '#FFFDF4',
        light: '#FFFEFA',
        dark: '#FCF9EE',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(221, 197, 111, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#DDC56F',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#DDC56F',
        gradientActiveTo: '#DDC56F',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFFDF4', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFFDF4', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFFDF4', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#FFFDF4!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFFDF4', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFFDF4', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFFDF4', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFFDF4', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#FFFDF4' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  msla_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#1375E1',
        dark: '#0D60BB',
        light: '#3C94F5',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#1375E1', //primary.main
        hover: '#0D60BB', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(19, 117, 225, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#1375E1',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#1375E1',
        gradientActiveTo: '#1375E1',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  msla_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#1375E1',
        dark: '#0D60BB',
        light: '#3C94F5',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#1375E1', //primary.main
        hover: '#0D60BB', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(19, 117, 225, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#1375E1',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#1375E1',
        gradientActiveTo: '#1375E1',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#12102E',
        default: '#191837',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  odag_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#019E69',
        dark: '#008458',
        light: '#00C07F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#019E69', //primary.main
        hover: '#008458', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(225, 240, 56, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#E1F038',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#E1F038',
        gradientActiveTo: '#E1F038',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  odag_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#E1F038',
        dark: '#D3E31E',
        light: '#F1FF54',
        contrastText: '#000000',
      },
      gradientButton: {
        background: '#E1F038', //primary.main
        hover: '#D3E31E', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(225, 240, 56, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#E1F038',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#E1F038',
        gradientActiveTo: '#E1F038',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  wnbh_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#1375E2',
        dark: '#0D60BB',
        light: '#3C94F5',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#1375E2', //primary.main
        hover: '#0D60BB', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(19, 117, 226, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#1375E2',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#1375E2',
        gradientActiveTo: '#1375E2',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  wnbh_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#1375E2',
        dark: '#0D60BB',
        light: '#3C94F5',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#1375E2', //primary.main
        hover: '#0D60BB', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(19, 117, 226, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#1375E2',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#1375E2',
        gradientActiveTo: '#1375E2',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  lxbp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#FF5532',
        dark: '#E83E1B',
        light: '#FF7458',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#FF5532', //primary.main
        hover: '#E83E1B', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 85, 50, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF5532',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF5532',
        gradientActiveTo: '#FF5532',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFEFD',
        default: '#FFF9F2',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  lxbp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#FF5532',
        dark: '#E83E1B',
        light: '#FF7458',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: '#FF5532', //primary.main
        hover: '#E83E1B', //primary.dark
      },
      secondary: {
        main: '#FFF5EB',
        light: '#FFFAF5',
        dark: '#FFEBD6',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 85, 50, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF5532',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF5532',
        gradientActiveTo: '#FF5532',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFF5EB', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFF5EB', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFF5EB', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#FFF5EB!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFF5EB', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFF5EB', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFF5EB', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFF5EB', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#FFF5EB' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  vph_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F2F740 0%, #F2F740 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  vph_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F8FF13 0%, #F8FF13 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        light: '#EFF3FF',
        dark: '#D4DFFF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#171716',
        default: '#262626',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#17AB71!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#17AB71' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  mrkp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#28CDA5',
        dark: '#17B891',
        light: '#26D8AD',
        contrastText: '#EAFFFA',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #28CDA5 0%, #28CDA5 100%)', //primary.main
        hover: '#17B891', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(0, 255, 194, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#00FFC2',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#00FFC2',
        gradientActiveTo: '#00FFC2',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  mrkp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#00FFC2',
        dark: '#00EBB3',
        light: '#16FFC7',
        contrastText: '#1E6B58',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #00FFC2 0%, #00FFC2 100%)', //primary.main
        hover: '#00EBB3', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(0, 255, 194, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#00FFC2',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#00FFC2',
        gradientActiveTo: '#00FFC2',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#17AB71' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  spna_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#EF477E',
        dark: '#D43166',
        light: '#FE699A',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #EF477E 0%, #EF477E 100%)', //primary.main
        hover: '#D43166', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#211F54',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(239, 71, 126, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#EF477E',
        headerBg: '#211F54',
        headerBorder: '#211F54',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#3C3A69',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#EF477E',
        gradientActiveTo: '#EF477E',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  spna_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#EF477E',
        dark: '#D43166',
        light: '#FE699A',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #EF477E 0%, #EF477E 100%)', //primary.main
        hover: '#D43166', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#211F54',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(239, 71, 126, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#EF477E',
        headerBg: '#211F54',
        headerBorder: '#211F54',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#3C3A69',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#EF477E',
        gradientActiveTo: '#EF477E',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#211F54',
        default: '#0E1234',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  nwnp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#E6272D',
        dark: '#EB5257',
        light: '#FD6F73',
        contrastText: '#FCE9EA',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #E6272D 0%, #E6272D 100%)', //primary.main
        hover: '#EB5257', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#202021',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(0, 255, 234, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#00FFEA',
        headerBg: '#202021',
        headerBorder: '#202021',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#00FFEA',
        gradientActiveTo: '#00FFEA',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  nwnp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#E6272D',
        dark: '#EB5257',
        light: '#FD6F73',
        contrastText: '#FCE9EA',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #E6272D 0%, #E6272D 100%)', //primary.main
        hover: '#EB5257', //primary.dark
      },
      secondary: {
        main: '#E4FFFE',
        dark: '#D4FDFF',
        light: '#EFFEFF',
        contrastText: '#001A17'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#202021',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(0, 255, 234, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#00FFEA',
        headerBg: '#202021',
        headerBorder: '#202021',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#00FFEA',
        gradientActiveTo: '#00FFEA',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#202021',
        default: '#161617',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4FFFE', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4FFFE', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4FFFE', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4FFFE!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4FFFE', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4FFFE', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4FFFE', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4FFFE', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4FFFE' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  btza_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#18AA52',
        dark: '#10843E',
        light: '#2FC96C',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #18AA52 0%, #18AA52 100%)', //primary.main
        hover: '#10843E', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(24, 170, 82, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#18AA52',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#18AA52',
        gradientActiveTo: '#18AA52',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  btza_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#18AA52',
        dark: '#10843E',
        light: '#2FC96C',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #18AA52 0%, #18AA52 100%)', //primary.main
        hover: '#10843E', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(24, 170, 82, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#18AA52',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#18AA52',
        gradientActiveTo: '#18AA52',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  stpr_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#EE3872',
        dark: '#D3265D',
        light: '#FC6998',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #EE3872 0%, #EE3872 100%)', //primary.main
        hover: '#D3265D', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(238, 56, 114, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#EE3872',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#EE3872',
        gradientActiveTo: '#EE3872',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  stpr_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#EE3872',
        dark: '#D3265D',
        light: '#FC6998',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #EE3872 0%, #EE3872 100%)', //primary.main
        hover: '#D3265D', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(238, 56, 114, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#EE3872',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#EE3872',
        gradientActiveTo: '#EE3872',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  gnbt_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#30F291',
        dark: '#01D370',
        light: '#4DFAA4',
        contrastText: '#0E1935',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #30F291 0%, #30F291 100%)', //primary.main
        hover: '#01D370', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#0E1935',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(48, 242, 145, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#30F291',
        headerBg: '#0E1935',
        headerBorder: '#0E1935',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1D2F5D',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#30F291',
        gradientActiveTo: '#30F291',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  gnbt_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#30F291',
        dark: '#01D370',
        light: '#4DFAA4',
        contrastText: '#0E1935',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #30F291 0%, #30F291 100%)', //primary.main
        hover: '#01D370', //primary.dark
      },
      secondary: {
        main: '#EDFFF6',
        dark: '#D9F8E9',
        light: '#F8FFFC',
        contrastText: 'rgba(14, 25, 53, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#0E1935',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(48, 242, 145, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#30F291',
        headerBg: '#0E1935',
        headerBorder: '#0E1935',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#1D2F5D',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#30F291',
        gradientActiveTo: '#06E99D',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#0E1935',
        default: '#030D26',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#EDFFF6', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#EDFFF6', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#EDFFF6', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#EDFFF6!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#EDFFF6', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#EDFFF6', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#EDFFF6', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#EDFFF6', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#EDFFF6' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  khlp_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#F91234',
        dark: '#D20926',
        light: '#FF4D67',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F91234 0%, #F91234 100%)', //primary.main
        hover: '#D20926', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(249, 18, 52, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F91234',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F91234',
        gradientActiveTo: '#F91234',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  khlp_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#F91234',
        dark: '#D20926',
        light: '#FF4D67',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F91234 0%, #F91234 100%)', //primary.main
        hover: '#D20926', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(249, 18, 52, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F91234',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F91234',
        gradientActiveTo: '#F91234',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E4ECFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E4ECFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E4ECFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E4ECFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  alfwn_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#BF2A2A',
        dark: '#AB1D1D',
        light: '#D84A4A',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #BF2A2A 0%, #BF2A2A 100%)', //primary.main
        hover: '#AB1D1D', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(191, 42, 42, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#BF2A2A',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#BF2A2A',
        gradientActiveTo: '#BF2A2A',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  alfwn_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#BF2A2A',
        dark: '#AB1D1D',
        light: '#D84A4A',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #BF2A2A 0%, #BF2A2A 100%)', //primary.main
        hover: '#AB1D1D', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(191, 42, 42, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#BF2A2A',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#BF2A2A',
        gradientActiveTo: '#BF2A2A',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#222222',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#BF2A2A', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#BF2A2A', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#BF2A2A', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#BF2A2A!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#BF2A2A', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#BF2A2A', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#BF2A2A', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#BF2A2A', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#BF2A2A' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  ownbt_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#85BFCF',
        dark: '#67ABBD',
        light: '#AFDCE9',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #85BFCF 0%, #85BFCF 100%)', //primary.main
        hover: '#67ABBD', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(192, 225, 234, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#C0E1EA',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#C0E1EA',
        gradientActiveTo: '#C0E1EA',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  ownbt_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#C0E1EA',
        dark: '#92C6D4',
        light: '#D9F4FB',
        contrastText: '#2D7183',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #C0E1EA 0%, #C0E1EA 100%)', //primary.main
        hover: '#92C6D4', //primary.dark
      },
      secondary: {
        main: '#F2FCFF',
        dark: '#EDFBFF',
        light: '#FBFEFF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(192, 225, 234, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#C0E1EA',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#C0E1EA',
        gradientActiveTo: '#C0E1EA',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F2FCFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F2FCFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F2FCFF', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F2FCFF!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F2FCFF', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F2FCFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F2FCFF', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F2FCFF', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F2FCFF' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  lirv_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#F5AC1E',
        dark: '#DB950D',
        light: '#FFC24B',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F5AC1E 0%, #F5AC1E 100%)', //primary.main
        hover: '#DB950D', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 172, 30, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F5AC1E',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F5AC1E',
        gradientActiveTo: '#F5AC1E',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  lirv_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#F5AC1E',
        dark: '#DB950D',
        light: '#FFC24B',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F5AC1E 0%, #F5AC1E 100%)', //primary.main
        hover: '#DB950D', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 172, 30, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F5AC1E',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F5AC1E',
        gradientActiveTo: '#F5AC1E',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5AC1E', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F5AC1E', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F5AC1E', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F5AC1E!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5AC1E', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F5AC1E', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F5AC1E', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5AC1E', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F5AC1E' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

  koor_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#F5A008',
        dark: '#E18B00',
        light: '#FFAF2F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F5A008 0%, #F5A008 100%)', //primary.main
        hover: '#E18B00', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 160, 8, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F5A008',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F5A008',
        gradientActiveTo: '#F5A008',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  koor_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#F5A008',
        dark: '#E18B00',
        light: '#FFAF2F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F5A008 0%, #F5A008 100%)', //primary.main
        hover: '#E18B00', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 160, 8, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F5A008',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F5A008',
        gradientActiveTo: '#F5A008',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5A008', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F5A008', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F5A008', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F5A008!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5A008', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F5A008', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F5A008', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F5A008', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F5A008' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

	lrma_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#FF9D00',
				dark: '#DB950D',
				light: '#FFC24B',
				contrastText: '#FFFFFF',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #FF9D00 0%, #FF9D00 100%)', //primary.main
				hover: '#DB950D', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				dark: '#000000',
				light: '#8D8D8D',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 157, 0, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FF9D00',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FF9D00',
				gradientActiveTo: '#FF9D00',
				scrollBg: 'rgba(0, 0, 0, 0.08)',
				scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F8',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	lrma_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#FF9D00',
				dark: '#DB950D',
				light: '#FFC24B',
				contrastText: '#FFFFFF',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #FF9D00 0%, #FF9D00 100%)', //primary.main
				hover: '#DB950D', //primary.dark
			},
			secondary: {
				main: '#E4ECFF',
				dark: '#D4DFFF',
				light: '#EFF3FF',
				contrastText: 'rgba(0, 0, 0, 0.87)',
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 157, 0, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FF9D00',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FF9D00',
				gradientActiveTo: '#FF9D00',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#363636',
				default: '#2D2D2D',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#E4ECFF!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#E4ECFF' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},

  slpkng_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#2A153E',
        dark: '#210E33',
        light: '#43265F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #2A153E 0%, #2A153E 100%)', //primary.main
        hover: '#210E33', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#2A153E',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 160, 8, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F0E9F4',
        headerBg: '#2A153E',
        headerBorder: '#2A153E',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#482E61',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F0E9F4',
        gradientActiveTo: '#F0E9F4',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F2EFF3',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  slpkng_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#F0E9F4',
        dark: '#F1E2FA',
        light: '#FDF9FF',
        contrastText: '#2A153E',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F0E9F4 0%, #F0E9F4 100%)', //primary.main
        hover: '#F1E2FA', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#2A153E',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(245, 160, 8, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F0E9F4',
        headerBg: '#2A153E',
        headerBorder: '#2A153E',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#482E61',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F0E9F4',
        gradientActiveTo: '#F0E9F4',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#140B1C',
        default: '#0A0A0A',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F0E9F4', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F0E9F4', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F0E9F4', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#F0E9F4!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F0E9F4', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#F0E9F4', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#F0E9F4', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#F0E9F4', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#F0E9F4' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },

	bsba_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#7AC50F',
        dark: '#6BB009',
        light: '#92DC2B',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #7AC50F 0%, #7AC50F 100%)', //primary.main
        hover: '#6BB009', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(122, 197, 15, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#7AC50F',
        headerBg: '#222222',
        headerBorder: '#222222',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#7AC50F',
        gradientActiveTo: '#7AC50F',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F2EFF3',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
	bsba_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#A7E14B',
        dark: '#8FCA32',
        light: '#BCF464',
        contrastText: '#222222',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #A7E14B 0%, #A7E14B 100%)', //primary.main
        hover: '#8FCA32', //primary.dark
      },
      secondary: {
        main: '#E1EBF9',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#222222',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(6, 233, 157, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#A7E14B',
        headerBg: '#A7E14B',
        headerBorder: '#A7E14B',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2E2E2E',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#A7E14B',
        gradientActiveTo: '#A7E14B',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#363636',
        default: '#2D2D2D',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E1EBF9', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E1EBF9', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E1EBF9', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#E1EBF9!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E1EBF9', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#E1EBF9', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#E1EBF9', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#E1EBF9', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#E1EBF9' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
	pmthagnt_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#17AB71',
				dark: '#10905E',
				light: '#00D68F',
				contrastText: '#FFFFFF',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #F2F740 0%, #F2F740 100%)', //primary.main
				hover: 'rgba(0,0,0,0.10)', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				light: '#8D8D8D',
				dark: '#000000',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#E57373',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#000000',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: '#282622',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#F2F740',
				headerBg: '#000000',
				headerBorder: '#000000',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#282622',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#F2F740',
				gradientActiveTo: '#F2F740',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F8',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	pmthagnt_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#17AB71',
				dark: '#10905E',
				light: '#00D68F',
				contrastText: '#FFFFFF',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #F8FF13 0%, #F8FF13 100%)', //primary.main
				hover: 'rgba(0,0,0,0.10)', //primary.dark
			},
			secondary: {
				main: '#E4ECFF',
				light: '#EFF3FF',
				dark: '#D4DFFF',
				contrastText: 'rgba(0, 0, 0, 0.87)'
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#E57373',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#000000',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: '#282622',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#F2F740',
				headerBg: '#000000',
				headerBorder: '#000000',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#282622',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#F2F740',
				gradientActiveTo: '#F2F740',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#171716',
				default: '#262626',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#17AB71', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#17AB71', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#17AB71', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#17AB71!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#17AB71', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#17AB71', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#17AB71', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#17AB71', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#17AB71' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},
	rooag_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#F3A646',
				dark: '#EA962E',
				light: '#FEB963',
				contrastText: '#31211B',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #F3A646 0%, #F3A646 100%)', //primary.main
				hover: '#EA962E', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				dark: '#000000',
				light: '#8D8D8D',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#231814',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(243, 166, 70, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#F3A646',
				headerBg: '#231814',
				headerBorder: '#231814',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#44332C',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFC884',
				gradientActiveTo: '#F3A646',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F8',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	rooag_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#F3A646',
				dark: '#F3A646',
				light: '#FEB963',
				contrastText: '#31211B',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #F3A646 0%, #F3A646 100%)', //primary.main
				hover: '#F3A646', //primary.dark
			},
			secondary: {
				main: '#E4ECFF',
				light: '#D4DFFF',
				dark: '#EFF3FF',
				contrastText: 'rgba(0, 0, 0, 0.87)'
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#231814',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(243, 166, 70, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#F3A646',
				headerBg: '#231814',
				headerBorder: '#231814',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#44332C',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFC884',
				gradientActiveTo: '#F3A646',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#222222',
				default: '#2D2D2D',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#E4ECFF!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#E4ECFF' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},
	aypr_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#FFB71B',
				dark: '#EFA300',
				light: '#FFBD2F',
				contrastText: '#271D08',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #FFB71B 0%, #FFB71B 100%)', //primary.main
				hover: '#EFA300', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				dark: '#000000',
				light: '#8D8D8D',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 183, 27, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FFB71B',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFB71B',
				gradientActiveTo: '#FFB71B',
				scrollBg: 'rgba(0, 0, 0, 0.08)',
				scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F8',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	aypr_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#FFB71B',
				dark: '#EFA300',
				light: '#FFBD2F',
				contrastText: '#271D08',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #FFB71B 0%, #FFB71B 100%)', //primary.main
				hover: '#EFA300', //primary.dark
			},
			secondary: {
				main: '#E4ECFF',
				light: '#D4DFFF',
				dark: '#EFF3FF',
				contrastText: 'rgba(0, 0, 0, 0.87)'
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 183, 27, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FFB71B',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFB71B',
				gradientActiveTo: '#FFB71B',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#363636',
				default: '#2D2D2D',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#E4ECFF!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#E4ECFF' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},
	turx_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#E5AE00',
				dark: '#CD9C00',
				light: '#FFC50D',
				contrastText: '#FFFFFF',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #E5AE00 0%, #E5AE00 100%)', //primary.main
				hover: '#CD9C00', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				dark: '#000000',
				light: '#8D8D8D',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FFBDB8',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 219, 16, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FFDB10',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFDB10',
				gradientActiveTo: '#FFDB10',
				scrollBg: 'rgba(0, 0, 0, 0.08)',
				scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F8',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	turx_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#FFDB10',
				dark: '#F4CF01',
				light: '#FFDE21',
				contrastText: '#4E3B00',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #FFDB10 0%, #FFDB10 100%)', //primary.main
				hover: '#F4CF01', //primary.dark
			},
			secondary: {
				main: '#E9EEE5',
				dark: '#DEE3DA',
				light: '#F3F6F1',
				contrastText: 'rgba(0, 0, 0, 0.87)'
			},
			error: {
				main: '#f44336',
				dark: '#D32F2F',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#222222',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(255, 219, 16, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#FFDB10',
				headerBg: '#222222',
				headerBorder: '#222222',
				headerContrast: '#FFFFFF',
				headerContrastHover: '#2E2E2E',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#FFDB10',
				gradientActiveTo: '#FFDB10',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#363636',
				default: '#2D2D2D',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E9EEE5', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E9EEE5', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E9EEE5', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#E9EEE5!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E9EEE5', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E9EEE5', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E9EEE5', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E9EEE5', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#E9EEE5' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},
	orbt_light: {
		spacing: 8,
		palette: {
			mode: 'light',
			text: {
				primary: '#292621',
				secondary: '#403C34',
				disabled: '#BDB8AD',
			},
			primary: {
				main: '#00B2FF',
				dark: '#00A0E5',
				light: '#11B7FF',
				contrastText: '#000000',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #00B2FF 0%, #00B2FF 100%)', //primary.main
				hover: '#00A0E5', //primary.dark
			},
			secondary: {
				main: '#1C1C1F',
				dark: '#000000',
				light: '#8D8D8D',
				contrastText: '#FFFFFF'
			},
			error: {
				main: '#F44336',
				dark: '#E31B0C',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: '#FFFFFF',
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: '#FFFFFF',
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			navigation: {
				menuBg: '#004F88',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(0, 178, 255, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#00B2FF',
				headerBg: '#004F88',
				headerBorder: '#004F88',
				headerContrast: '#FFFFFF',
				headerContrastHover: 'rgba(255, 255, 255, 0.04)',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#00B2FF',
				gradientActiveTo: '#00B2FF',
				scrollBg: 'rgba(0, 0, 0, 0.08)',
				scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
			},
			other: {
				divider: 'rgba(34, 34, 34, 0.12)',
				outlinedBorder: 'rgba(0, 0, 0, 0.23)',
				filledInputBackground: 'rgba(0, 0, 0, 0.06)',
				standardInputLine: 'rgba(34, 34, 34, 0.09)',
				snackbar: '#323232',
				ratingActive: '#F69B5A',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			selected: {
				main: 'rgba(0, 0, 0, 0.08)',
			},
			background: {
				paper: '#F7F9FF',
				default: '#EFF6FF',
			},
			grey: {
				50: '#FAFAFA',
				100: '#F5F5F5',
				200: '#EEEEEE',
				300: '#E0E0E0',
				400: '#BDBDBD',
				500: '#9E9E9E',
				600: '#757575',
				700: '#616161',
				800: '#424242',
				900: '#212121',
			}
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
		},
	},
	orbt_dark: {
		spacing: 8,
		palette: {
			mode: 'dark',
			text: {
				primary: '#FFFFFF',
				secondary: 'rgba(255, 255, 255, 0.70)',
				disabled: 'rgba(255, 255, 255, 0.50)',
			},
			primary: {
				main: '#00B2FF',
				dark: '#00A0E5',
				light: '#11B7FF',
				contrastText: '#000000',
			},
			gradientButton: {
				background: 'linear-gradient(180deg, #00B2FF 0%, #00B2FF 100%)', //primary.main
				hover: '#00A0E5', //primary.dark
			},
			secondary: {
				main: '#E4ECFF',
				dark: '#D4DFFF',
				light: '#EFF3FF',
				contrastText: 'rgba(0, 0, 0, 0.87)'
			},
			error: {
				main: '#F44336',
				dark: '#D32F2F',
				light: '#FFBDB8',
				contrastText: '#FFFFFF'
			},
			warning: {
				main: '#FF9800',
				dark: '#F57C00',
				light: '#FFE0B2',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			info: {
				main: '#03A9F4',
				dark: '#0288D1',
				light: '#B3E5FC',
				contrastText: 'rgba(255, 255, 255, 0.87)'
			},
			success: {
				main: '#8BC34A',
				dark: '#689F38',
				light: '#DCEDC8',
				contrastText: '#FFFFFF',
			},
			white: {
				main: '#fff',
				contrastText: '#ffffff',
			},
			common: {
				white: '#FFFFFF',
				black: '#222222',
			},
			navigation: {
				menuBg: '#004F88',
				menuHover: 'rgba(255, 255, 255, 0.04)',
				menuActive: 'rgba(0, 178, 255, 0.08)',
				menuContrast: '#FFFFFF',
				menuContrastActive: '#00B2FF',
				headerBg: '#004F88',
				headerBorder: '#004F88',
				headerContrast: '#FFFFFF',
				headerContrastHover: 'rgba(255, 255, 255, 0.08)',
				gradientFrom: '#FFFFFF',
				gradientTo: '#FFFFFF',
				gradientActiveFrom: '#00B2FF',
				gradientActiveTo: '#00B2FF',
				scrollBg: 'rgba(255, 255, 255, 0.08)',
				scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
			},
			other: {
				divider: 'rgba(255, 255, 255, 0.12)',
				outlinedBorder: 'rgba(255, 255, 255, 0.23)',
				filledInputBackground: 'rgba(255, 255, 255, 0.06)',
				standardInputLine: 'rgba(255, 255, 255, 0.09)',
				snackbar: '#FFFFFF',
				ratingActive: '#FFB400',
				tooltip: 'rgba(97, 97, 97, 0.90)',
				searchBg: 'rgba(255, 255, 255, 0.15)',
			},
			divider: 'rgba(255, 255, 255, 0.12)',
			selected: {
				main: 'rgba(255, 255, 255, 0.08)',
			},
			action: {
				active: 'rgba(255,255,255,0.54)', // primary.light
				hover: 'rgba(255,255,255,0.04)', // primary.dark
				hoverOpacity: 0.08,
				selected: 'rgba(255,255,255,0.08)',
				selectedOpacity: 0.16,
				disabled: 'rgba(255,255,255,0.26)', // text.disabled
				disabledOpacity: 0.38,
				disabledBackground: 'rgba(255,255,255, 0.12)',
				focus: 'rgba(255,255,255, 0.12)',
				focusOpacity: 0.12,
				activatedOpacity: 0.24,
			},
			background: {
				paper: '#11111F',
				default: '#0D0D16',
			},
			grey: {
				50: '#212121',
				100: '#424242',
				200: '#616161',
				300: '#757575',
				400: '#9E9E9E',
				500: '#BDBDBD',
				600: '#E0E0E0',
				700: '#EEEEEE',
				800: '#F5F5F5',
				900: '#FAFAFA',
				A100: '#D5D5D5',
				A200: '#AAAAAA',
				A400: '#616161',
				A700: '#303030',
			},
		},
		typography: {
			fontFamily: `'Roboto', 'Arial', sans-serif`,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1025,
				lg: 1200,
				xl: 1440,
				xlg: 1272,
			}
		},
		components: {
			MuiButton: MuiButtonsOverrides,
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							"& > fieldset": {
								borderColor: '#E4ECFF!important', //secondary.main
							},
						},

						[`& .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
							"&:hover": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							},
						},
						"& > label": {
							"&.Mui-focused": {
								color: '#E4ECFF', //secondary.main
							},
						}
					}
				}
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						"& .MuiSelect-select": {
							"& > fieldset": {
								borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
							},
							"&.Mui-focused": {
								"& > fieldset": {
									borderColor: '#E4ECFF', //secondary.main
								}
							}
						},
					},
				}
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							color: '#E4ECFF' //secondary.main
						},
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: 'rgba(97,97,97,0.9)',
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: {
						//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
		},
	},
  mngd_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#FF9B32',
        dark: '#F0A000',
        light: '#FFB115',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(299deg, #EB5F63 -27.19%, #FEE500 119.19%)',
        hover: '#F0A000', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#11111F',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 155, 50, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF9B32',
        headerBg: '#11111F',
        headerBorder: '#11111F',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2B2B40',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF9B32',
        gradientActiveTo: '#EB5F63',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  mngd_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#FF9B32',
        dark: '#F0A000',
        light: '#FFB115',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(299deg, #EB5F63 -27.19%, #FEE500 119.19%)',
        hover: '#F0A000', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#11111F',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 155, 50, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FF9B32',
        headerBg: '#11111F',
        headerBorder: '#11111F',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#2B2B40',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FF9B32',
        gradientActiveTo: '#EB5F63',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#11111F',
        default: '#0D0D16',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FF9B32', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FF9B32', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FF9B32', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#FF9B32!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FF9B32', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FF9B32', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FF9B32', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FF9B32', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#FF9B32' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  pmvip_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F2F740 0%, #F2F740 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        light: '#8D8D8D',
        dark: '#000000',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  pmvip_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#17AB71',
        dark: '#10905E',
        light: '#00D68F',
        contrastText: '#FFFFFF',
      },
      gradientButton: {
        background: 'linear-gradient(180deg, #F8FF13 0%, #F8FF13 100%)', //primary.main
        hover: 'rgba(0,0,0,0.10)', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        light: '#EFF3FF',
        dark: '#D4DFFF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#E57373',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#000000',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: '#282622',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#F2F740',
        headerBg: '#000000',
        headerBorder: '#000000',
        headerContrast: '#FFFFFF',
        headerContrastHover: '#282622',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#F2F740',
        gradientActiveTo: '#F2F740',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#171716',
        default: '#262626',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#17AB71!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#17AB71', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#17AB71', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#17AB71' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  crpb_light: {
    spacing: 8,
    palette: {
      mode: 'light',
      text: {
        primary: '#292621',
        secondary: '#403C34',
        disabled: '#BDB8AD',
      },
      primary: {
        main: '#FFB600',
        dark: '#F2AC00',
        light: '#FFBD19',
        contrastText: '#0C0D11',
      },
      gradientButton: {
        background: '#FFB600', //primary.main
        hover: '#F2AC00', //primary.dark
      },
      secondary: {
        main: '#1C1C1F',
        dark: '#000000',
        light: '#8D8D8D',
        contrastText: '#FFFFFF'
      },
      error: {
        main: '#F44336',
        dark: '#E31B0C',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      navigation: {
        menuBg: '#0C0D11',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 182, 0, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFB600',
        headerBg: '#0C0D11',
        headerBorder: '#0C0D11',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FFB600',
        gradientActiveTo: '#FFB600',
        scrollBg: 'rgba(0, 0, 0, 0.08)',
        scrollTrackBg: 'rgba(85, 85, 85, 0.3)',
      },
      other: {
        divider: 'rgba(34, 34, 34, 0.12)',
        outlinedBorder: 'rgba(0, 0, 0, 0.23)',
        filledInputBackground: 'rgba(0, 0, 0, 0.06)',
        standardInputLine: 'rgba(34, 34, 34, 0.09)',
        snackbar: '#323232',
        ratingActive: '#F69B5A',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      selected: {
        main: 'rgba(0, 0, 0, 0.08)',
      },
      background: {
        paper: '#FFFFFF',
        default: '#F7F7F8',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      }
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
    },
  },
  crpb_dark: {
    spacing: 8,
    palette: {
      mode: 'dark',
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.70)',
        disabled: 'rgba(255, 255, 255, 0.50)',
      },
      primary: {
        main: '#FFB600',
        dark: '#F2AC00',
        light: '#FFBD19',
        contrastText: '#0C0D11',
      },
      gradientButton: {
        background: '#FFB600', //primary.main
        hover: '#F2AC00', //primary.dark
      },
      secondary: {
        main: '#E4ECFF',
        dark: '#D4DFFF',
        light: '#EFF3FF',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      error: {
        main: '#F44336',
        dark: '#D32F2F',
        light: '#FFBDB8',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: '#FF9800',
        dark: '#F57C00',
        light: '#FFE0B2',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      info: {
        main: '#03A9F4',
        dark: '#0288D1',
        light: '#B3E5FC',
        contrastText: 'rgba(255, 255, 255, 0.87)'
      },
      success: {
        main: '#8BC34A',
        dark: '#689F38',
        light: '#DCEDC8',
        contrastText: '#FFFFFF',
      },
      white: {
        main: '#fff',
        contrastText: '#ffffff',
      },
      common: {
        white: '#FFFFFF',
        black: '#222222',
      },
      navigation: {
        menuBg: '#0C0D11',
        menuHover: 'rgba(255, 255, 255, 0.04)',
        menuActive: 'rgba(255, 182, 0, 0.08)',
        menuContrast: '#FFFFFF',
        menuContrastActive: '#FFB600',
        headerBg: '#0C0D11',
        headerBorder: '#0C0D11',
        headerContrast: '#FFFFFF',
        headerContrastHover: 'rgba(255, 255, 255, 0.04)',
        gradientFrom: '#FFFFFF',
        gradientTo: '#FFFFFF',
        gradientActiveFrom: '#FFB600',
        gradientActiveTo: '#FFB600',
        scrollBg: 'rgba(255, 255, 255, 0.08)',
        scrollTrackBg: 'rgba(255, 255, 255, 0.3)',
      },
      other: {
        divider: 'rgba(255, 255, 255, 0.12)',
        outlinedBorder: 'rgba(255, 255, 255, 0.23)',
        filledInputBackground: 'rgba(255, 255, 255, 0.06)',
        standardInputLine: 'rgba(255, 255, 255, 0.09)',
        snackbar: '#FFFFFF',
        ratingActive: '#FFB400',
        tooltip: 'rgba(97, 97, 97, 0.90)',
        searchBg: 'rgba(255, 255, 255, 0.15)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      selected: {
        main: 'rgba(255, 255, 255, 0.08)',
      },
      action: {
        active: 'rgba(255,255,255,0.54)', // primary.light
        hover: 'rgba(255,255,255,0.04)', // primary.dark
        hoverOpacity: 0.08,
        selected: 'rgba(255,255,255,0.08)',
        selectedOpacity: 0.16,
        disabled: 'rgba(255,255,255,0.26)', // text.disabled
        disabledOpacity: 0.38,
        disabledBackground: 'rgba(255,255,255, 0.12)',
        focus: 'rgba(255,255,255, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      background: {
        paper: '#0C0D11',
        default: '#171920',
      },
      grey: {
        50: '#212121',
        100: '#424242',
        200: '#616161',
        300: '#757575',
        400: '#9E9E9E',
        500: '#BDBDBD',
        600: '#E0E0E0',
        700: '#EEEEEE',
        800: '#F5F5F5',
        900: '#FAFAFA',
        A100: '#D5D5D5',
        A200: '#AAAAAA',
        A400: '#616161',
        A700: '#303030',
      },
    },
    typography: {
      fontFamily: `'Roboto', 'Arial', sans-serif`,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1025,
        lg: 1200,
        xl: 1440,
        xlg: 1272,
      }
    },
    components: {
      MuiButton: MuiButtonsOverrides,
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFB600', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFFFFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFB600', //secondary.main
              },
            }
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              "& > fieldset": {
                borderColor: '#FFB600!important', //secondary.main
              },
            },

            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFB600', //secondary.main
                }
              },
              "&:hover": {
                "& > fieldset": {
                  borderColor: '#FFFFFF', //secondary.main
                }
              },
            },
            "& > label": {
              "&.Mui-focused": {
                color: '#FFB600', //secondary.main
              },
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            "& .MuiSelect-select": {
              "& > fieldset": {
                borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
              },
              "&.Mui-focused": {
                "& > fieldset": {
                  borderColor: '#FFB600', //secondary.main
                }
              }
            },
          },
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: '#FFB600' //secondary.main
            },
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'rgba(97,97,97,0.9)',
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            //borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
}

const theme: Theme = (() => {
  const currentTheme = createTheme(themes[appTheme]);

  return currentTheme;
})();

export default {...theme};
